import React, { useState, useRef, useEffect, useMemo } from "react";
import {
    Dialog,
    DialogContent,
    DialogActions,
    Typography,
    TextField,
    Button,
    IconButton,
    Box,
    Checkbox,
    FormControlLabel,
    Tooltip,
    Select,
    MenuItem,
    CircularProgress,
    Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../../../../Components/SnackBar";
import { apiPostRequest, apiPutRequest } from "../../../../Helpers/Requests";
import endpoints from "../../../../Helpers/Endpoints";
import MissingCategoryModal from "./MissingCategoryModal";
import {
    productModalPaperStyle,
    productModalHeaderStyle,
    productModalTitleStyle,
    productModalHeaderIconsStyle,
    productModalContentStyle,
    productModalFieldLabel,
    productModalTextField,
    productModalTextFieldError,
    productModalFooterStyle,
    productModalFooterRowStyle,
    backWithoutSavingBtnStyle,
    createProductBtnStyle,
    cantFindLinkStyle,
    cantFindLinkMobileStyle,
    cantFindLinkDesktopStyle,
    imageUploadGridStyle,
    imageUploadItemStyle,
    imageUploadRemoveStyle,
    imageUploadAddBtnStyle,
    tooltipIconStyle,
    categoryDropdownStyle,
    getCategoryMenuProps,
} from "./styles";
import { categoryNamespaces } from "../../../../Helpers/CategoryNamespaces";
import OrderMinimumHint from "../../../../Components/OrderMinimumHint";

const MAX_IMAGE_SIZE_MB = 15;
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/jpg", "image/webp"];

const ProductModal = ({
    open,
    onClose,
    product,
    isEdit,
    isDuplicate,
    categories,
    onProductSaved,
    tagSuggestions = [],
    vertical = "products",
}) => {
    const api = endpoints.catalog(vertical);
    // A digital item is one the supplier sends the buyer electronically — that
    // only makes sense in the store catalog. Restaurant menu items are always
    // physical food, so the option is hidden for that vertical (COMY1-331);
    // the BE ignores the field there too.
    const supportsDigital = vertical !== "restaurants";
    const categoryNs = categoryNamespaces(vertical);
    const { t, i18n } = useTranslation(["supplier-products", ...categoryNs]);
    const categoryMenuProps = getCategoryMenuProps(i18n.dir() === "rtl");
    const snackbar = useSnackbar();

    // Form state
    const [name, setName] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("");
    const [description, setDescription] = useState("");
    const [additionalTerms, setAdditionalTerms] = useState("");
    const [price, setPrice] = useState("");
    const [isDigital, setIsDigital] = useState(false);
    const [privateSku, setPrivateSku] = useState("");
    const [productTags, setProductTags] = useState([]);
    const [tagInput, setTagInput] = useState("");
    const [highlightedTagIndex, setHighlightedTagIndex] = useState(-1);
    const isTagDropdownSelectionRef = useRef(false);
    const tagBlurTimeoutRef = useRef(null);
    const tagDropdownRef = useRef(null);
    // What the supplier typed, without the trailing comma they might have used
    // as a separator.
    const trimmedTagInput = tagInput.trim().replace(/,+$/, "").trim();
    // Dropdown options: the existing tags matching what was typed, plus an
    // "add <typed tag>" entry when the text isn't an existing tag yet — so a
    // brand-new tag can be added by clicking the dropdown, exactly like picking
    // an existing one, instead of having to type a comma (COMY1-340).
    const tagOptions = useMemo(() => {
        const query = trimmedTagInput.toLowerCase();
        if (!query) return [];
        const selected = productTags.map((tag) => tag.toLowerCase());
        const matches = tagSuggestions
            .filter(
                (tag) =>
                    tag.toLowerCase().includes(query) &&
                    !selected.includes(tag.toLowerCase())
            )
            .map((tag) => ({ value: tag, isNew: false }));
        const alreadyExists =
            selected.includes(query) ||
            tagSuggestions.some((tag) => tag.toLowerCase() === query);
        return alreadyExists
            ? matches
            : [...matches, { value: trimmedTagInput, isNew: true }];
    }, [trimmedTagInput, tagSuggestions, productTags]);
    // Adds a tag from the dropdown / keyboard and resets the input.
    const commitTag = (tag) => {
        const newTag = tag?.trim();
        if (!newTag) return;
        const exists = productTags.some(
            (existing) => existing.toLowerCase() === newTag.toLowerCase()
        );
        if (!exists) setProductTags((prev) => [...prev, newTag]);
        setTagInput("");
        setHighlightedTagIndex(-1);
    };
    const [mandatoryInfoEnabled, setMandatoryInfoEnabled] = useState(false);
    const [mandatoryInfoText, setMandatoryInfoText] = useState("");

    // Images
    const [mainImage, setMainImage] = useState(null); // File or URL string
    const [mainImagePreview, setMainImagePreview] = useState(null);
    const [extraImages, setExtraImages] = useState([]); // Array of { file?: File, url?: string, preview: string }

    // Validation
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    // Missing category modal
    const [showMissingCategoryModal, setShowMissingCategoryModal] =
        useState(false);

    const mainImageInputRef = useRef(null);
    const extraImageInputRef = useRef(null);

    useEffect(() => {
        return () => {
            if (tagBlurTimeoutRef.current) clearTimeout(tagBlurTimeoutRef.current);
        };
    }, []);

    // Prefill when editing or duplicating
    useEffect(() => {
        if (product) {
            setName(product.name || "");
            setSelectedCategory(product.category_id || "");
            setDescription(product.description || "");
            setAdditionalTerms(product.additional_terms || "");
            setPrice(product.price ? String(product.price) : "");
            setIsDigital(product.is_digital || false);
            setPrivateSku(product.private_sku || "");
            setProductTags(
                product.tags
                    ? product.tags.split(",").map((t) => t.trim()).filter(Boolean)
                    : []
            );
            setMandatoryInfoEnabled(!!product.client_input_label);
            setMandatoryInfoText(product.client_input_label || "");

            if (product.main_image_url) {
                setMainImagePreview(product.main_image_url);
                setMainImage(product.main_image_url);
            }

            if (product.extra_images && product.extra_images.length > 0) {
                setExtraImages(
                    product.extra_images.map((img) => ({
                        url: img.url || img,
                        preview: img.url || img,
                    }))
                );
            }
        }
    }, [product]);

    // Build flat list for category dropdown (parent > child)
    const categoryOptions = [];
    if (categories && categories.length > 0) {
        categories.forEach((cat) => {
            categoryOptions.push({
                id: cat.id,
                name: cat.name,
                parentName: null,
                isParent: true,
            });
            if (cat.subcategories) {
                cat.subcategories.forEach((sub) => {
                    categoryOptions.push({
                        id: sub.id,
                        name: sub.name,
                        parentName: cat.name,
                        isParent: false,
                        parentId: cat.id,
                    });
                });
            }
        });
    }

    const formatCategoryOption = (option) => {
        const translated = t(option.name, { ns: categoryNs, defaultValue: option.name });
        if (!option.parentName) return translated;
        const translatedParent = t(option.parentName, { ns: categoryNs, defaultValue: option.parentName });
        return `${translatedParent} > ${translated}`;
    };

    const validateForm = () => {
        const newErrors = {};

        if (!name.trim()) {
            newErrors.name = t("modal.fields.product_name_required");
        }
        if (!selectedCategory) {
            newErrors.category = t("modal.fields.category_required");
        }
        if (!price || Number(price) <= 0) {
            newErrors.price = t("modal.fields.product_price_min");
        } else if (!/^\d+(\.\d{1,2})?$/.test(price)) {
            newErrors.price = t("modal.fields.product_price_invalid");
        }
        if (!mainImage && !mainImagePreview) {
            newErrors.mainImage = t("modal.fields.main_image_required");
        }
        if (mandatoryInfoEnabled && !mandatoryInfoText.trim()) {
            newErrors.mandatoryInfo = t("modal.fields.user_mandatory_info_required");
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleImageValidation = (file) => {
        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            snackbar(t("modal.fields.image_invalid_type"), "error");
            return false;
        }
        if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
            snackbar(t("modal.fields.image_too_large"), "error");
            return false;
        }
        return true;
    };

    const handleMainImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!handleImageValidation(file)) return;

        setMainImage(file);
        setMainImagePreview(URL.createObjectURL(file));
        setErrors((prev) => ({ ...prev, mainImage: undefined }));
    };

    const handleRemoveMainImage = () => {
        setMainImage(null);
        setMainImagePreview(null);
    };

    const handleExtraImageChange = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        const validFiles = files.filter((file) => handleImageValidation(file));
        const remaining = 5 - extraImages.length;
        const filesToAdd = validFiles.slice(0, remaining);

        if (validFiles.length > remaining) {
            snackbar(t("modal.fields.image_max_count"), "error");
        }

        setExtraImages((prev) => [
            ...prev,
            ...filesToAdd.map((file) => ({
                file,
                preview: URL.createObjectURL(file),
            })),
        ]);
        e.target.value = "";
    };

    const handleRemoveExtraImage = (index) => {
        setExtraImages((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            setSubmitting(true);

            const formData = new FormData();
            formData.append("name", name.trim());
            formData.append("category_id", selectedCategory);
            formData.append("description", description);
            formData.append("additional_terms", additionalTerms);
            formData.append("price", price);
            formData.append("is_digital", supportsDigital && isDigital);
            formData.append("private_sku", privateSku);
            formData.append("tags", productTags.join(", "));
            formData.append(
                "client_input_label",
                mandatoryInfoEnabled ? mandatoryInfoText : ""
            );

            // Main image
            if (mainImage instanceof File) {
                formData.append("main_image", mainImage);
            } else if (typeof mainImage === "string") {
                formData.append("main_image_url", mainImage);
            }

            // Extra images
            extraImages.forEach((img, index) => {
                if (img.file) {
                    formData.append("extra_images", img.file);
                } else if (img.url) {
                    formData.append(`extra_image_urls[${index}]`, img.url);
                }
            });

            let response;
            const formHeaders = { "Content-Type": "multipart/form-data" };
            if (isEdit && product?.id) {
                response = await apiPutRequest(
                    api.updateItem(product.id),
                    formData,
                    formHeaders
                );
            } else {
                response = await apiPostRequest(
                    api.createItem,
                    formData,
                    formHeaders
                );
            }

            if (response.status === 200 || response.status === 201) {
                // BE sets `fulfillment_missing: true` on create when the
                // supplier hasn't configured pickup or any delivery
                // district. Forward it so the parent can decide whether
                // to show the "configure fulfillment first" prompt.
                onProductSaved(
                    response.data.product,
                    isEdit,
                    !isEdit && response.data?.fulfillment_missing === true
                );
            }
        } catch (error) {
            console.error("Error saving product:", error);
            const status = error?.response?.status;
            const reason = error?.response?.data?.message;
            if (status === 413) {
                snackbar(t("modal.errors.payload_too_large"), "error");
            } else if (reason && status >= 400 && status < 500) {
                // Surface the actual reason for client errors (e.g. validation)
                // so the supplier knows what to fix, instead of a dead-end
                // "please try again". 5xx messages can be raw server/DB text,
                // so those fall back to the generic message below.
                snackbar(
                    t("modal.errors.save_failed_reason", { reason }),
                    "error"
                );
            } else {
                snackbar(t("modal.errors.save_failed"), "error");
            }
        } finally {
            setSubmitting(false);
        }
    };

    const modalTitle = isEdit
        ? t("modal.edit_title")
        : t("modal.add_title");
    const submitButtonText = isEdit
        ? t("modal.save_button")
        : t("modal.create_button");

    return (
        <>
            <Dialog
                open={open}
                onClose={(_event, reason) => {
                    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
                    onClose();
                }}
                disableEscapeKeyDown
                PaperProps={{ sx: productModalPaperStyle }}
                fullWidth
                maxWidth="md"
            >
                {/* Header */}
                <Box sx={productModalHeaderStyle}>
                    <Typography sx={productModalTitleStyle}>{modalTitle}</Typography>
                    <Box sx={productModalHeaderIconsStyle}>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon sx={{ fontSize: "20px" }} />
                        </IconButton>
                    </Box>
                </Box>

                {/* Content - Two column grid */}
                <DialogContent sx={{ p: 0, overflow: "auto" }}>
                    <Box sx={productModalContentStyle}>
                        {/* LEFT COLUMN */}
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                            {/* Product Name */}
                            <Box>
                                <Typography sx={productModalFieldLabel}>
                                    {t("modal.fields.product_name")} *
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={name}
                                    onChange={(e) => {
                                        setName(e.target.value);
                                        if (errors.name)
                                            setErrors((prev) => ({ ...prev, name: undefined }));
                                    }}
                                    error={!!errors.name}
                                    sx={errors.name ? productModalTextFieldError : productModalTextField}
                                />
                                {errors.name && (
                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: "4px" }}>
                                        {errors.name}
                                    </Typography>
                                )}
                            </Box>

                            {/* Category & Subcategory */}
                            <Box>
                                <Typography sx={productModalFieldLabel}>
                                    {t("modal.fields.category_subcategory")} *
                                </Typography>
                                <Select
                                    fullWidth
                                    size="small"
                                    displayEmpty
                                    value={selectedCategory}
                                    onChange={(e) => {
                                        setSelectedCategory(e.target.value);
                                        if (errors.category)
                                            setErrors((prev) => ({ ...prev, category: undefined }));
                                    }}
                                    error={!!errors.category}
                                    sx={categoryDropdownStyle}
                                    MenuProps={categoryMenuProps}
                                    renderValue={(selected) => {
                                        if (!selected) {
                                            return (
                                                <Typography sx={{ color: "rgba(62, 63, 76, 0.4)", fontSize: "14px" }}>
                                                    {t("modal.fields.category_subcategory")}
                                                </Typography>
                                            );
                                        }
                                        const option = categoryOptions.find(
                                            (o) => o.id === selected
                                        );
                                        return option ? formatCategoryOption(option) : "";
                                    }}
                                >
                                    {categoryOptions.map((option) => (
                                        <MenuItem
                                            key={option.id}
                                            value={option.id}
                                            disabled={option.isParent}
                                            sx={{
                                                pl: option.isParent ? "16px" : "32px",
                                                fontWeight: option.isParent ? 600 : 400,
                                                fontSize: "14px",
                                                opacity: option.isParent ? "0.7 !important" : 1,
                                            }}
                                        >
                                            {t(option.name, { ns: categoryNs, defaultValue: option.name })}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.category && (
                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: "4px" }}>
                                        {errors.category}
                                    </Typography>
                                )}
                            </Box>

                            {/* Product Description */}
                            <Box>
                                <Typography sx={productModalFieldLabel}>
                                    {t("modal.fields.product_description")}
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    multiline
                                    minRows={3}
                                    maxRows={3}
                                    inputProps={{ className: "custom-scrollbar" }}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder={t("modal.fields.product_description_placeholder")}
                                    sx={productModalTextField}
                                />
                            </Box>

                            {/* Additional Terms */}
                            <Box>
                                <Typography sx={productModalFieldLabel}>
                                    {t("modal.fields.additional_terms")}
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    multiline
                                    minRows={3}
                                    maxRows={3}
                                    inputProps={{ className: "custom-scrollbar" }}
                                    value={additionalTerms}
                                    onChange={(e) => setAdditionalTerms(e.target.value)}
                                    placeholder={t("modal.fields.additional_terms_placeholder")}
                                    sx={productModalTextField}
                                />
                            </Box>

                            {/* Product Price */}
                            <Box>
                                <Typography sx={productModalFieldLabel}>
                                    {t("modal.fields.product_price")} *
                                </Typography>
                                <TextField
                                    size="small"
                                    value={price}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        // Allow only valid price input
                                        if (val === "" || /^\d*\.?\d{0,2}$/.test(val)) {
                                            setPrice(val);
                                            if (errors.price)
                                                setErrors((prev) => ({ ...prev, price: undefined }));
                                        }
                                    }}
                                    error={!!errors.price}
                                    InputProps={{
                                        startAdornment: (
                                            <Typography sx={{ mr: "4px", color: "#6C727F" }}>₪</Typography>
                                        ),
                                    }}
                                    sx={{
                                        width: "140px",
                                        ...(errors.price ? productModalTextFieldError : productModalTextField),
                                    }}
                                />
                                {/* The item may cost whatever the supplier
                                    wants; the ORDER is what has a minimum. */}
                                <Box sx={{ display: "block" }}>
                                    <OrderMinimumHint />
                                </Box>
                                {errors.price && (
                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: "4px" }}>
                                        {errors.price}
                                    </Typography>
                                )}
                            </Box>

                            {/* Digital Product checkbox — stores only */}
                            {supportsDigital && (
                            <Box>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isDigital}
                                            onChange={(e) => setIsDigital(e.target.checked)}
                                            sx={{
                                                color: "#248B70",
                                                "&.Mui-checked": { color: "#248B70" },
                                            }}
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                                                {t("modal.fields.digital_product")}
                                            </Typography>
                                            <Tooltip title={t("modal.tooltips.digital_product")} arrow>
                                                <HelpOutlineIcon sx={tooltipIconStyle} />
                                            </Tooltip>
                                        </Box>
                                    }
                                />
                            </Box>
                            )}
                        </Box>

                        {/* RIGHT COLUMN */}
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                            {/* Private SKU */}
                            <Box>
                                <Box sx={{ display: "flex", alignItems: "center", mb: "6px" }}>
                                    <Typography sx={productModalFieldLabel}>
                                        {t("modal.fields.private_sku")}
                                    </Typography>
                                    <Tooltip title={t("modal.tooltips.private_sku")} arrow>
                                        <HelpOutlineIcon sx={tooltipIconStyle} />
                                    </Tooltip>
                                </Box>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={privateSku}
                                    onChange={(e) => setPrivateSku(e.target.value)}
                                    sx={productModalTextField}
                                />
                            </Box>

                            {/* Product Tags */}
                            <Box>
                                <Box sx={{ display: "flex", alignItems: "center", mb: "6px" }}>
                                    <Typography sx={productModalFieldLabel}>
                                        {t("modal.fields.product_tags")}
                                    </Typography>
                                    <Tooltip title={t("modal.tooltips.product_tags")} arrow>
                                        <HelpOutlineIcon sx={tooltipIconStyle} />
                                    </Tooltip>
                                </Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: "6px",
                                        minHeight: "42px",
                                        px: "12px",
                                        py: "8px",
                                        border: "1px solid",
                                        borderRadius: "8px",
                                        borderColor: "rgba(230, 228, 235, 1)",
                                        "&:hover": {
                                            borderColor: "rgba(36, 139, 112, 1)",
                                        },
                                        "&:focus-within": {
                                            borderColor: "rgba(36, 139, 112, 1)",
                                            boxShadow: "0 0 0 1px rgba(36, 139, 112, 1)",
                                        },
                                    }}
                                >
                                    {productTags.map((tag, index) => (
                                        <Chip
                                            key={`${tag}-${index}`}
                                            label={tag}
                                            onDelete={() =>
                                                setProductTags((prev) =>
                                                    prev.filter((_, i) => i !== index)
                                                )
                                            }
                                            deleteIcon={<CloseIcon />}
                                            sx={{
                                                borderRadius: "6px",
                                                backgroundColor: "rgba(47, 43, 61, 0.08)",
                                                color: "rgba(47, 43, 61, 0.9)",
                                                height: "24px",
                                                "& .MuiChip-label": {
                                                    px: "8px",
                                                    fontSize: "15px",
                                                    fontWeight: 400,
                                                    lineHeight: "20px",
                                                },
                                                "& .MuiChip-deleteIcon": {
                                                    color: "rgba(62, 63, 76, 0.7)",
                                                    fontSize: "16px",
                                                    "&:hover": {
                                                        color: "rgba(62, 63, 76, 0.7)",
                                                    },
                                                },
                                            }}
                                        />
                                    ))}
                                    <input
                                        type="text"
                                        value={tagInput}
                                        onChange={(e) => {
                                            setTagInput(e.target.value);
                                            setHighlightedTagIndex(-1);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === ",") {
                                                if (!trimmedTagInput) return;
                                                e.preventDefault();
                                                commitTag(
                                                    highlightedTagIndex >= 0
                                                        ? tagOptions[highlightedTagIndex]?.value
                                                        : trimmedTagInput
                                                );
                                            } else if (e.key === "ArrowDown") {
                                                if (tagOptions.length === 0) return;
                                                e.preventDefault();
                                                setHighlightedTagIndex((prev) => {
                                                    const next =
                                                        prev < tagOptions.length - 1
                                                            ? prev + 1
                                                            : prev;
                                                    const node =
                                                        tagDropdownRef.current?.children[next];
                                                    if (node) node.scrollIntoView({ block: "nearest" });
                                                    return next;
                                                });
                                            } else if (e.key === "ArrowUp") {
                                                if (tagOptions.length === 0) return;
                                                e.preventDefault();
                                                setHighlightedTagIndex((prev) => {
                                                    const next = prev > 0 ? prev - 1 : -1;
                                                    if (next >= 0) {
                                                        const node =
                                                            tagDropdownRef.current?.children[next];
                                                        if (node)
                                                            node.scrollIntoView({ block: "nearest" });
                                                    }
                                                    return next;
                                                });
                                            } else if (
                                                e.key === "Backspace" &&
                                                !tagInput &&
                                                productTags.length > 0
                                            ) {
                                                setProductTags((prev) => prev.slice(0, -1));
                                            }
                                        }}
                                        onBlur={() => {
                                            if (tagBlurTimeoutRef.current) {
                                                clearTimeout(tagBlurTimeoutRef.current);
                                            }
                                            tagBlurTimeoutRef.current = setTimeout(() => {
                                                if (
                                                    trimmedTagInput &&
                                                    !isTagDropdownSelectionRef.current
                                                ) {
                                                    commitTag(trimmedTagInput);
                                                }
                                                setTagInput("");
                                                setHighlightedTagIndex(-1);
                                                isTagDropdownSelectionRef.current = false;
                                            }, 100);
                                        }}
                                        placeholder={
                                            productTags.length === 0
                                                ? t("modal.fields.product_tags")
                                                : ""
                                        }
                                        style={{
                                            flex: 1,
                                            minWidth: "60px",
                                            border: "none",
                                            outline: "none",
                                            backgroundColor: "transparent",
                                            fontSize: "15px",
                                        }}
                                    />
                                </Box>
                                {tagOptions.length > 0 && (
                                    <Box sx={{ position: "relative", zIndex: 1 }}>
                                        <Box
                                            ref={tagDropdownRef}
                                            sx={{
                                                position: "absolute",
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                maxHeight: "200px",
                                                overflowY: "auto",
                                                backgroundColor: "#fff",
                                                borderRadius: "8px",
                                                boxShadow:
                                                    "0 4px 20px rgba(0,0,0,0.1)",
                                                border: "1px solid #e0e0e0",
                                                zIndex: 1300,
                                            }}
                                        >
                                            {tagOptions.map((opt, index) => (
                                                <Box
                                                    key={`${opt.isNew ? "new" : "existing"}-${opt.value}`}
                                                    onMouseDown={() => {
                                                        isTagDropdownSelectionRef.current = true;
                                                        commitTag(opt.value);
                                                    }}
                                                    sx={{
                                                        p: 1.5,
                                                        cursor: "pointer",
                                                        fontSize: "15px",
                                                        backgroundColor:
                                                            highlightedTagIndex === index
                                                                ? "#e0e0e0"
                                                                : "transparent",
                                                        "&:hover": {
                                                            backgroundColor: "#f5f5f5",
                                                        },
                                                    }}
                                                >
                                                    {opt.isNew
                                                        ? t("modal.fields.add_tag", {
                                                              tag: opt.value,
                                                          })
                                                        : opt.value}
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </Box>

                            {/* User Mandatory Information */}
                            <Box>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={mandatoryInfoEnabled}
                                            onChange={(e) =>
                                                setMandatoryInfoEnabled(e.target.checked)
                                            }
                                            sx={{
                                                color: "#248B70",
                                                "&.Mui-checked": { color: "#248B70" },
                                            }}
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                                                {t("modal.fields.user_mandatory_info")}
                                            </Typography>
                                            <Tooltip
                                                title={t("modal.tooltips.user_mandatory_info")}
                                                arrow
                                            >
                                                <HelpOutlineIcon sx={tooltipIconStyle} />
                                            </Tooltip>
                                        </Box>
                                    }
                                />
                                {mandatoryInfoEnabled && (
                                    <Box>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            value={mandatoryInfoText}
                                            onChange={(e) => {
                                                setMandatoryInfoText(e.target.value);
                                                if (errors.mandatoryInfo)
                                                    setErrors((prev) => ({
                                                        ...prev,
                                                        mandatoryInfo: undefined,
                                                    }));
                                            }}
                                            placeholder={t(
                                                "modal.fields.user_mandatory_info_placeholder"
                                            )}
                                            error={!!errors.mandatoryInfo}
                                            sx={
                                                errors.mandatoryInfo
                                                    ? productModalTextFieldError
                                                    : productModalTextField
                                            }
                                        />
                                        {errors.mandatoryInfo && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: "4px" }}>
                                                {errors.mandatoryInfo}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Box>

                            {/* Main Image */}
                            <Box>
                                <Typography sx={productModalFieldLabel}>
                                    {t("modal.fields.main_image")} *
                                </Typography>
                                <Box sx={imageUploadGridStyle}>
                                    {mainImagePreview ? (
                                        <Box sx={imageUploadItemStyle}>
                                            <img
                                                src={mainImagePreview}
                                                alt="main"
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                }}
                                            />
                                            <Box
                                                sx={imageUploadRemoveStyle}
                                                onClick={handleRemoveMainImage}
                                            >
                                                <CloseIcon
                                                    sx={{ fontSize: "12px", color: "#fff" }}
                                                />
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Box
                                            sx={imageUploadAddBtnStyle}
                                            onClick={() => mainImageInputRef.current?.click()}
                                        >
                                            <img
                                                src="/icons/camera-icon.svg"
                                                alt="upload"
                                                width="20"
                                                height="20"
                                                style={{ opacity: 0.5 }}
                                            />
                                            <Typography
                                                sx={{ fontSize: "10px", color: "#6C727F" }}
                                            >
                                                {t("modal.fields.add_photo")}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                                <input
                                    ref={mainImageInputRef}
                                    type="file"
                                    accept={ALLOWED_IMAGE_TYPES.join(",")}
                                    style={{ display: "none" }}
                                    onChange={handleMainImageChange}
                                />
                                {errors.mainImage && (
                                    <Typography
                                        sx={{ color: "#d32f2f", fontSize: "12px", mt: "4px" }}
                                    >
                                        {errors.mainImage}
                                    </Typography>
                                )}
                            </Box>

                            {/* Extra Images */}
                            <Box>
                                <Typography sx={productModalFieldLabel}>
                                    {t("modal.fields.extra_images")}
                                </Typography>
                                <Box sx={imageUploadGridStyle}>
                                    {extraImages.map((img, index) => (
                                        <Box key={index} sx={imageUploadItemStyle}>
                                            <img
                                                src={img.preview}
                                                alt={`extra-${index}`}
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                }}
                                            />
                                            <Box
                                                sx={imageUploadRemoveStyle}
                                                onClick={() => handleRemoveExtraImage(index)}
                                            >
                                                <CloseIcon
                                                    sx={{ fontSize: "12px", color: "#fff" }}
                                                />
                                            </Box>
                                        </Box>
                                    ))}
                                    <Box
                                        sx={imageUploadAddBtnStyle}
                                        onClick={() => extraImageInputRef.current?.click()}
                                    >
                                        <img
                                            src="/icons/camera-icon.svg"
                                            alt="upload"
                                            width="20"
                                            height="20"
                                            style={{ opacity: 0.5 }}
                                        />
                                        <Typography sx={{ fontSize: "10px", color: "#6C727F" }}>
                                            {t("modal.fields.add_photo")}
                                        </Typography>
                                    </Box>
                                </Box>
                                <input
                                    ref={extraImageInputRef}
                                    type="file"
                                    accept={ALLOWED_IMAGE_TYPES.join(",")}
                                    multiple
                                    style={{ display: "none" }}
                                    onChange={handleExtraImageChange}
                                />
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>

                {/* Footer */}
                <Box sx={productModalFooterStyle}>
                    {/* On mobile the link sits on its own line so the buttons keep their space */}
                    <Typography sx={cantFindLinkMobileStyle}>
                        {t("modal.cant_find_category")}{" "}
                        <Typography
                            component="a"
                            onClick={() => setShowMissingCategoryModal(true)}
                            sx={cantFindLinkStyle["& a"]}
                        >
                            {t("modal.click_here")}
                        </Typography>
                    </Typography>

                    <Box sx={productModalFooterRowStyle}>
                        <Button
                            variant="contained"
                            onClick={onClose}
                            sx={backWithoutSavingBtnStyle}
                        >
                            {t("modal.back_without_saving")}
                        </Button>

                        <Typography sx={cantFindLinkDesktopStyle}>
                            {t("modal.cant_find_category")}{" "}
                            <Typography
                                component="a"
                                onClick={() => setShowMissingCategoryModal(true)}
                                sx={cantFindLinkStyle["& a"]}
                            >
                                {t("modal.click_here")}
                            </Typography>
                        </Typography>

                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={submitting}
                            sx={createProductBtnStyle}
                        >
                            {submitButtonText}
                            {submitting && (
                                <CircularProgress
                                    size={18}
                                    sx={{ color: "#fff", ml: "8px" }}
                                />
                            )}
                        </Button>
                    </Box>
                </Box>
            </Dialog>

            {/* Missing Category sub-modal */}
            {showMissingCategoryModal && (
                <MissingCategoryModal
                    vertical={vertical}
                    open={showMissingCategoryModal}
                    onClose={() => setShowMissingCategoryModal(false)}
                />
            )}
        </>
    );
};

export default ProductModal;
